import { DestroyRef, ErrorHandler, inject, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, type Observable } from 'rxjs';
import type { BusEvent } from './bus-event';
import { EventDispatcher } from './event-dispatcher';

/**
 * Base class for per-area event facades.
 *
 * Each feature area subclasses this with its own flat, past-tense, `readonly`
 * discriminated union `E`. Scoping is achieved purely by *which facade you
 * inject* — DI surfaces only that area's events in intellisense. The runtime is
 * unified per app (one shared `EventDispatcher`) so events actually cross areas;
 * the *type surface* is segmented per area.
 *
 * Subclasses are expected to be `providedIn: 'root'` and to call `super()` from
 * within an injection context (so the shared dispatcher resolves).
 */
export abstract class AreaEvents<E extends BusEvent> {
  readonly #dispatcher = inject(EventDispatcher);

  /**
   * This area's namespace prefix (e.g. `auth`), used by the dev-mode provenance
   * assertion in `dispatch`. Subclasses set it to the segment before the first
   * `/` in their union's `type` discriminants. It is type-level documentation
   * of the area, never read in production builds.
   */
  protected abstract readonly namespace: string;

  /**
   * Push one of this area's events onto the shared ordered stream.
   *
   * In dev mode only, asserts `event.type` is namespaced under this facade's
   * `namespace` (e.g. an `AuthEvents` instance must not dispatch a `calendar/*`
   * event). Guarded by `isDevMode()`, so it is a no-op in production builds. The
   * type system already rejects off-namespace events absent a cast, so this is a
   * cast-escape backstop (see ADR-0003), not provenance enforcement.
   */
  dispatch(event: E): void {
    if (isDevMode() && !event.type.startsWith(`${this.namespace}/`)) {
      throw new Error(
        `EventBus: ${this.constructor.name} dispatched "${event.type}", which is not namespaced under "${this.namespace}/". An area facade may only dispatch its own area's events.`,
      );
    }
    this.#dispatcher.dispatch(event);
  }

  /**
   * A pure, lazy, composable `Observable` of just the events whose `type`
   * matches `type`, narrowed to that member of the union.
   *
   * Callable anywhere — it has no teardown side effects and does not depend on
   * an injection context. Subscribers own their own teardown and error
   * handling. For the leak-proof, injection-context convenience, use `listen`.
   */
  on<T extends E['type']>(type: T): Observable<Extract<E, { type: T }>> {
    return this.#dispatcher.events$.pipe(
      filter((event): event is Extract<E, { type: T }> => event.type === type),
    );
  }

  /**
   * Injection-context-only convenience that subscribes `handler` to `on(type)`
   * and tears down automatically when the *consumer's* `DestroyRef` fires
   * (captured here via `takeUntilDestroyed`). Leak-proof.
   *
   * Must be called from an injection context (constructor / field initializer /
   * `runInInjectionContext`), otherwise `DestroyRef` cannot be resolved.
   */
  listen<T extends E['type']>(
    type: T,
    handler: (event: Extract<E, { type: T }>) => void,
  ): void {
    const destroyRef = inject(DestroyRef);
    const errorHandler = inject(ErrorHandler);
    this.on(type)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((event) => {
        // Isolate each handler invocation: a throwing listener must neither
        // kill the underlying subscription (which would silently stop future
        // deliveries to *this* listener) nor reach the shared Subject (which is
        // immortal by construction). Sibling listeners are independent
        // subscriptions, so they receive the same event regardless. Errors are
        // forwarded to Angular's `ErrorHandler` rather than swallowed.
        try {
          handler(event);
        } catch (error) {
          errorHandler.handleError(error);
        }
      });
  }
}
